import { useState, useEffect } from 'react'; 
import { useNavigate } from 'react-router-dom';
import { auth, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import useLimits from '../../hooks/useLimits';

const UploadContent = ({ isOpen, onClose, onSubmit, onStartGlobalLoading, onStopGlobalLoading }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const navigate = useNavigate(); // Initialize useNavigate hook
  const { uploads, fileUploadLimit } = useLimits();
  const isLimitReached = uploads !== undefined && uploads <= 0;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setApiError(null);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Allow only PDF
      const allowedExts = ['pdf'];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowedExts.includes(ext)) {
        e.target.value = null;
        setSelectedFile(null);
        setApiError('Please select a PDF file.');
        return;
      }
      // Check file size against fileUploadLimit (in MB)
      if (fileUploadLimit && file.size > fileUploadLimit * 1024 * 1024) {
        setSelectedFile(null);
        setApiError(`File size exceeds your limit of ${fileUploadLimit} MB.`);
        return;
      }
      setSelectedFile(file);
      setApiError(null);
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) {
      setApiError('Please select a file to upload.');
      return;
    }
    // Check uploads limit
    if (uploads !== undefined && uploads <= 0) {
      setApiError('You have reached your daily upload limit.');
      return;
    }
    try {
      if (!auth.currentUser) {
        setApiError('Authentication required. Please sign in.');
        return;
      }
      setIsLoading(true);
      setApiError(null);

      // Close modal first, then start the scoped card overlay after a tick
      onClose();
      setTimeout(() => onStartGlobalLoading?.('Uploading document...'), 30);
      
      // 1) Upload the file to Firebase Storage
      try {
        const safeName = selectedFile.name.replace(/[^\w.-]/g, '_');
        const storagePath = `documents/${auth.currentUser.uid}/${Date.now()}_${safeName}`;
        
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, selectedFile);
        
        const downloadURL = await getDownloadURL(storageRef);

        // 2) Get ID token (same as YouTubeURL)
        const idToken = await auth.currentUser.getIdToken(true);

        // 3) Call extract_document Cloud Function - mirror YouTubeURL pattern (GET + Authorization + uid=idToken)
        let extracted = null;
        let contentId = null;
        
        try {
          const PROCESS_URL_API = process.env.REACT_APP_PROCESS_URL_API;
          const endpoint = `${PROCESS_URL_API}?url=${encodeURIComponent(downloadURL)}&uid=${idToken}`;

          const resp = await fetch(endpoint, {
            method: 'GET',
            mode: 'cors',
            credentials: 'omit',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            }
          });

          if (!resp.ok) {
            const errorText = await resp.text();
            throw new Error(`API error (${resp.status}): ${errorText}`);
          }

          extracted = await resp.json();
          
          // Get document_id from API response
          contentId = extracted.document_id;
        } catch (apiErr) {
          // Surface error via toast, keep going
          onStopGlobalLoading?.({ success: false, message: `Content extraction failed: ${apiErr.message}` });
        }

        // 4) Forward to onSubmit (pass storageUrl + extracted data)
        const fileType = selectedFile.name.split('.').pop().toLowerCase();
        
        const result = await onSubmit({
          type: 'document',
          fileName: selectedFile.name,
          fileType,
          storageUrl: downloadURL,
          // keep both data and content for compatibility with existing consumers
          data: extracted || null,
          content: extracted?.text || extracted || "",
          source_type: "Document URL",
          source_url: downloadURL,
          contentId // Pass the document ID for redirection
        });

        // 5) Navigate and stop loader
        if (contentId) {
          onStopGlobalLoading?.({ success: true, message: 'Upload complete. Opening chat...' });
          setTimeout(() => {
            navigate(`/c/${contentId}`);
          }, 50);
        } else if (result && result.contentId) {
          onStopGlobalLoading?.({ success: true, message: 'Upload complete. Opening chat...' });
          setTimeout(() => {
            navigate(`/c/${result.contentId}`);
          }, 50);
        } else {
          onStopGlobalLoading?.({ success: false, message: 'Upload processed but no chat id returned.' });
        }
      } catch (storageError) {
        onStopGlobalLoading?.({ success: false, message: `File upload failed: ${storageError.message}` });
        setApiError(`File upload failed: ${storageError.message}`);
      }
    } catch (error) {
      onStopGlobalLoading?.({ success: false, message: error.message || 'An error occurred during upload.' });
      setApiError(error.message || 'An error occurred during upload.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-70" 
        onClick={onClose}
      ></div>

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        <div className="p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Upload Document</h2>

          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Select a PDF file
            </label>
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              disabled={isLimitReached}
            />
            {isLimitReached && (
              <div className="text-red-600 text-sm" role="alert" aria-live="polite">
                You have reached your daily upload limit.
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={!selectedFile || isLoading || isLimitReached}
              className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Uploading...' : 'Upload'}
            </button>
          </div>

          {apiError && !isLoading && (
            <div className="text-red-600 mt-2">{apiError}</div>
          )}
        </div>
      </div>
    </div>
  );
};



export default UploadContent;

